import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, MessageCircle } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

const LOGO_URL = "/assets/images/logo.png";
const WHATSAPP_NUMBER = "+41779938989";

const navLinks = [
  { href: '#start', label: 'Start' },
  { href: '#leistungen', label: 'Leistungen' },
  { href: '#ueber-uns', label: 'Über uns' },
  { href: '#faq', label: 'FAQ' },
];

export const Navigation = ({ onOpenWizard }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);

    if (location.pathname === '/') {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const openWhatsApp = () => {
    const message = encodeURIComponent("Guten Tag, ich interessiere mich für Ihre Sicherheitsdienstleistungen.");
    window.open(`https://wa.me/${WHATSAPP_NUMBER.replace(/\+/g, '')}?text=${message}`, '_blank');
  };

  return (
    <>
      <motion.nav
        data-testid="main-navigation"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled
          ? 'bg-[#0B0F19]/90 backdrop-blur-xl border-b border-[#1E293B]'
          : 'bg-transparent'
          }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-24 md:h-28">
            {/* Logo removed as requested - Layout adjusted to left-align nav */}

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="nav-link text-sm font-medium tracking-wide uppercase"
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </a>
              ))}
            </div>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-4">
              <button
                onClick={onOpenWizard}
                className="btn-secondary text-xs"
                data-testid="nav-anfrage-btn"
              >
                Anfrage starten
              </button>
              <button
                onClick={openWhatsApp}
                className="btn-primary flex items-center gap-2 text-xs"
                data-testid="nav-whatsapp-btn"
              >
                <MessageCircle size={16} />
                WhatsApp
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-[#F1F5F9]"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="mobile-menu lg:hidden"
            data-testid="mobile-menu"
          >
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="absolute top-6 right-6 p-2 text-[#F1F5F9]"
            >
              <X size={28} />
            </button>

            <img
              src={LOGO_URL}
              alt="Praesidium Protection"
              className="h-24 w-auto mb-8"
            />

            {navLinks.map((link, index) => (
              <motion.a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </motion.a>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-col gap-4 mt-8"
            >
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  onOpenWizard();
                }}
                className="btn-secondary"
                data-testid="mobile-anfrage-btn"
              >
                Anfrage starten
              </button>
              <button
                onClick={openWhatsApp}
                className="btn-primary flex items-center justify-center gap-2"
                data-testid="mobile-whatsapp-btn"
              >
                <MessageCircle size={18} />
                WhatsApp Kontakt
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
